import { get } from "lodash";
import { ExternalLink, Upload } from "lucide-react";
import React, { useEffect } from "react";

import { Button } from "@shared/ui";
import CarrierMark from "../components/CarrierMark";
import StepBody from "../components/StepBody";
import StepFooter from "../components/StepFooter";
import StepHeading from "../components/StepHeading";
import { getIntegrationConfig } from "../lib/utility";

const LoginSupport = ({
  step,
  nextStep,
  loginInformation,
  session,
  posthog,
  integration,
  setLoginAttempts,
  previousStep,
}) => {
  const sessionCarrierId = get(loginInformation, "carrier.id");
  const isShortcutEnabled = get(session, "config.manual.shortcut", false);

  // Retrieve the registration and recovery urls from the integration config in the carrier config
  const integrationConfig = getIntegrationConfig(
    session,
    loginInformation,
    integration
  );

  const registerUrl = get(integrationConfig, "registerUrl", false);
  const recoveryUrl = get(integrationConfig, "recoveryUrl", false);

  const returnToLogin = () => {
    // A recovery-link click replaces the old reset acknowledgement. Visiting
    // support or going Back must preserve the local attempt allowance.
    setLoginAttempts((attempts) => ({ ...attempts, [sessionCarrierId]: 0 }));
    nextStep("login");
  };

  useEffect(() => {
    posthog.capture("$pageview", {
      step,
      sessionCarrierId,
    });
  });

  return (
    <>
      <StepBody>
        <CarrierMark loginInformation={loginInformation} />
        <StepHeading
          title="Can’t log in?"
          description={
            isShortcutEnabled
              ? "Reset your login information or upload a document to continue."
              : "Reset your login information to continue."
          }
        />
        {(recoveryUrl || registerUrl) && (
          <div className="flex flex-col gap-3">
            {recoveryUrl && (
              <ExternalOption
                url={recoveryUrl}
                onClick={() => {
                  // Capture posthog event for forgot password
                  posthog.capture("forgot-password-click", {
                    step,
                    previousStep,
                    sessionCarrierId,
                  });
                  returnToLogin();
                }}
                text={"Reset your password"}
              />
            )}
            {registerUrl && (
              <ExternalOption
                url={registerUrl}
                onClick={() => {
                  // Capture posthog event for register
                  posthog.capture("register-click", {
                    step,
                    previousStep,
                    sessionCarrierId,
                  });
                  returnToLogin();
                }}
                text={"Create an online account"}
              />
            )}
            <p className="pt-1 text-body-lg text-ink-secondary">
              These open in a new tab. Come back here when you’re done to continue.
            </p>
          </div>
        )}
      </StepBody>
      {isShortcutEnabled && (
        <StepFooter>
          <Button
            type="button"
            onClick={() => {
              nextStep("manual-account");
            }}
          >
            <Upload aria-hidden className="size-4" strokeWidth={2} />
            Upload a policy document instead
          </Button>
        </StepFooter>
      )}
    </>
  );
};

const ExternalOption = ({ url, onClick, text }) => (
  <a
    href={url}
    target="_blank"
    onClick={onClick}
    className="flex h-control-lg items-center justify-between gap-3 rounded border border-border px-4 text-body-medium text-ink transition-colors hover:bg-surface-muted active:bg-surface-pressed"
  >
    {text}
    <ExternalLink aria-hidden className="size-4 shrink-0 text-ink-secondary" strokeWidth={1.75} />
    <span className="sr-only">(opens in a new tab)</span>
  </a>
);

export default LoginSupport;
